/**
 * @boli/core - Auth Service
 *
 * Platform-agnostic authentication logic for Boli Live.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 *
 * Key Features:
 * - Google OAuth only (no email/password to reduce security overhead)
 * - Token management with secure storage abstraction
 * - Session persistence across platforms
 * - KYC tier management
 *
 * SECURITY WARNING:
 * This service uses an ISecureStorage abstraction for token storage.
 * - On Web: The default implementation uses localStorage which is vulnerable to XSS attacks.
 *   For production, consider using httpOnly cookies set by the server instead.
 * - On Mobile: Use SecureStorage/Keychain which provides hardware-backed encryption.
 *
 * Recommended security improvements for Web:
 * 1. Store tokens in httpOnly cookies (set by server, not accessible via JS)
 * 2. Implement CSRF protection with SameSite cookies
 * 3. Use short-lived access tokens with refresh token rotation
 * 4. Add Content-Security-Policy headers to mitigate XSS
 */

import type {
  User,
  AuthCredentials,
  AuthSession,
  ApiResponse,
  KYCStatus,
  KYCTier,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IAuthService {
  /**
   * Initialize the auth service
   */
  initialize(): Promise<void>;

  /**
   * Sign in with Google OAuth
   */
  signInWithGoogle(idToken: string): Promise<ApiResponse<AuthSession>>;

  /**
   * Sign out current user
   */
  signOut(): Promise<ApiResponse<void>>;

  /**
   * Get current authenticated user
   */
  getCurrentUser(): Promise<User | null>;

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): Promise<boolean>;

  /**
   * Refresh authentication token
   */
  refreshToken(): Promise<ApiResponse<AuthSession>>;

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(callback: (user: User | null) => void): () => void;

  /**
   * Get current auth token
   */
  getToken(): Promise<string | null>;

  /**
   * Get user's KYC status
   */
  getKYCStatus(): Promise<KYCStatus>;

  /**
   * Submit KYC documents
   */
  submitKYC(documents: KYCDocuments): Promise<ApiResponse<KYCStatus>>;
}

export interface KYCDocuments {
  cnicFront?: string; // Base64 or URL
  cnicBack?: string;
  bankStatement?: string;
  addressProof?: string;
}

/**
 * Abstract interface for secure storage
 * Web: localStorage/sessionStorage with encryption
 * Mobile: SecureStorage/Keychain
 */
export interface ISecureStorage {
  setItem(key: string, value: string): Promise<void>;
  getItem(key: string): Promise<string | null>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

export interface AuthServiceConfig {
  apiBaseUrl: string;
  googleClientId: string;
  tokenRefreshThreshold: number; // Refresh when X seconds remaining
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_AUTH_CONFIG: AuthServiceConfig = {
  apiBaseUrl: '',
  googleClientId: '',
  tokenRefreshThreshold: 300, // Refresh 5 minutes before expiry
};

// ============================================================================
// STORAGE KEYS
// ============================================================================

const STORAGE_KEYS = {
  AUTH_TOKEN: 'boli_auth_token',
  REFRESH_TOKEN: 'boli_refresh_token',
  USER: 'boli_user',
  EXPIRES_AT: 'boli_expires_at',
};

// ============================================================================
// AUTH SERVICE IMPLEMENTATION
// ============================================================================

export class AuthService implements IAuthService {
  private config: AuthServiceConfig;
  private storage: ISecureStorage;
  private fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  private authStateCallbacks: Set<(user: User | null) => void> = new Set();
  private currentUser: User | null = null;
  private refreshTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(
    config: Partial<AuthServiceConfig>,
    storage: ISecureStorage,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>
  ) {
    this.config = { ...DEFAULT_AUTH_CONFIG, ...config };
    this.storage = storage;
    this.fetchAdapter = fetchAdapter;
  }

  /**
   * Initialize the auth service - check for existing session
   */
  async initialize(): Promise<void> {
    try {
      const token = await this.storage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const userJson = await this.storage.getItem(STORAGE_KEYS.USER);
      const expiresAt = await this.storage.getItem(STORAGE_KEYS.EXPIRES_AT);

      if (token && userJson && expiresAt) {
        const expiry = parseInt(expiresAt, 10);

        // Check if token is still valid
        if (Date.now() < expiry) {
          this.currentUser = JSON.parse(userJson);
          this.notifyAuthStateChange(this.currentUser);
          this.scheduleTokenRefresh(expiry);
        } else {
          // Try to refresh the token
          await this.refreshToken();
        }
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      await this.clearStorage();
    }
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle(idToken: string): Promise<ApiResponse<AuthSession>> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/auth/google`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: {
            code: errorData.code || 'AUTH_FAILED',
            message: errorData.message || 'Authentication failed',
          },
        };
      }

      const session: AuthSession = await response.json();

      // Store session data
      await this.storeSession(session);

      // Update current user
      this.currentUser = session.user;
      this.notifyAuthStateChange(this.currentUser);

      // Schedule token refresh
      this.scheduleTokenRefresh(session.expiresAt);

      return { success: true, data: session };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<ApiResponse<void>> {
    try {
      const token = await this.storage.getItem(STORAGE_KEYS.AUTH_TOKEN);

      if (token) {
        // Notify server (fire and forget)
        this.fetchAdapter(`${this.config.apiBaseUrl}/api/auth/signout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }).catch(() => {});
      }

      // Clear local state
      await this.clearStorage();
      this.currentUser = null;
      this.notifyAuthStateChange(null);

      if (this.refreshTimeoutId) {
        clearTimeout(this.refreshTimeoutId);
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'SIGNOUT_ERROR',
          message: error instanceof Error ? error.message : 'Sign out failed',
        },
      };
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User | null> {
    if (this.currentUser) {
      return this.currentUser;
    }

    const userJson = await this.storage.getItem(STORAGE_KEYS.USER);
    if (userJson) {
      this.currentUser = JSON.parse(userJson);
    }

    return this.currentUser;
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const token = await this.storage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const expiresAt = await this.storage.getItem(STORAGE_KEYS.EXPIRES_AT);

    if (!token || !expiresAt) {
      return false;
    }

    return Date.now() < parseInt(expiresAt, 10);
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<ApiResponse<AuthSession>> {
    try {
      const refreshToken = await this.storage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      if (!refreshToken) {
        return {
          success: false,
          error: {
            code: 'NO_REFRESH_TOKEN',
            message: 'No refresh token available',
          },
        };
      }

      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/auth/refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        }
      );

      if (!response.ok) {
        // Refresh failed, clear session
        await this.clearStorage();
        this.currentUser = null;
        this.notifyAuthStateChange(null);

        return {
          success: false,
          error: {
            code: 'REFRESH_FAILED',
            message: 'Token refresh failed',
          },
        };
      }

      const session: AuthSession = await response.json();

      // Store new session
      await this.storeSession(session);

      // Update current user
      this.currentUser = session.user;
      this.notifyAuthStateChange(this.currentUser);

      // Schedule next refresh
      this.scheduleTokenRefresh(session.expiresAt);

      return { success: true, data: session };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Listen to auth state changes
   */
  onAuthStateChange(callback: (user: User | null) => void): () => void {
    this.authStateCallbacks.add(callback);

    // Immediately invoke with current state
    callback(this.currentUser);

    // Return cleanup function
    return () => {
      this.authStateCallbacks.delete(callback);
    };
  }

  /**
   * Get current auth token
   */
  async getToken(): Promise<string | null> {
    return this.storage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  /**
   * Get user's KYC status
   */
  async getKYCStatus(): Promise<KYCStatus> {
    if (!this.currentUser) {
      return {
        tier: 'tier1',
        cnicUploaded: false,
        cnicVerified: false,
        bankVerified: false,
        addressProof: false,
        maxBidAmount: 25000, // PKR limit for tier1
        maxListingAmount: 25000,
      };
    }

    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/users/kyc-status`,
        {
          headers: {
            Authorization: `Bearer ${await this.getToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch KYC status');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get KYC status:', error);
      return {
        tier: 'tier1',
        cnicUploaded: false,
        cnicVerified: false,
        bankVerified: false,
        addressProof: false,
        maxBidAmount: 25000,
        maxListingAmount: 25000,
      };
    }
  }

  /**
   * Submit KYC documents
   */
  async submitKYC(documents: KYCDocuments): Promise<ApiResponse<KYCStatus>> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/users/kyc`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await this.getToken()}`,
          },
          body: JSON.stringify(documents),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: {
            code: errorData.code || 'KYC_FAILED',
            message: errorData.message || 'KYC submission failed',
          },
        };
      }

      const kycStatus = await response.json();
      return { success: true, data: kycStatus };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  private notifyAuthStateChange(user: User | null): void {
    this.authStateCallbacks.forEach((callback) => {
      try {
        callback(user);
      } catch (error) {
        console.error('Auth state callback error:', error);
      }
    });
  }

  private async storeSession(session: AuthSession): Promise<void> {
    await Promise.all([
      this.storage.setItem(STORAGE_KEYS.AUTH_TOKEN, session.token),
      this.storage.setItem(STORAGE_KEYS.USER, JSON.stringify(session.user)),
      this.storage.setItem(STORAGE_KEYS.EXPIRES_AT, session.expiresAt.toString()),
      session.refreshToken
        ? this.storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, session.refreshToken)
        : Promise.resolve(),
    ]);
  }

  private async clearStorage(): Promise<void> {
    await Promise.all([
      this.storage.removeItem(STORAGE_KEYS.AUTH_TOKEN),
      this.storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN),
      this.storage.removeItem(STORAGE_KEYS.USER),
      this.storage.removeItem(STORAGE_KEYS.EXPIRES_AT),
    ]);
  }

  private scheduleTokenRefresh(expiresAt: number): void {
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }

    const refreshTime =
      expiresAt - Date.now() - this.config.tokenRefreshThreshold * 1000;

    if (refreshTime > 0) {
      this.refreshTimeoutId = setTimeout(() => {
        this.refreshToken().catch(console.error);
      }, refreshTime);
    }
  }

  /**
   * Create authenticated fetch wrapper
   */
  async authenticatedFetch(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const token = await this.getToken();

    const headers = new Headers(options.headers);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return this.fetchAdapter(url, { ...options, headers });
  }
}

// ============================================================================
// AUTH SERVICE FACTORY
// ============================================================================

let authServiceInstance: AuthService | null = null;

export function createAuthService(
  config: Partial<AuthServiceConfig>,
  storage: ISecureStorage,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>
): AuthService {
  authServiceInstance = new AuthService(config, storage, fetchAdapter);
  return authServiceInstance;
}

export function getAuthService(): AuthService {
  if (!authServiceInstance) {
    throw new Error('AuthService not initialized. Call createAuthService first.');
  }
  return authServiceInstance;
}
